import { logApiCall } from './log-collector';

const STORAGE_PREFIX = 'dayan:';

export const Storage = {
  get<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown): void {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
    } catch { /* quota exceeded */ }
  },
  remove(key: string): void {
    localStorage.removeItem(STORAGE_PREFIX + key);
  },
};

type EventCallback = (event: { type: string; payload: unknown }) => void;

const _listeners = new Map<string, Set<EventCallback>>();

export const EventBus = {
  on(type: string, cb: EventCallback): () => void {
    if (!_listeners.has(type)) _listeners.set(type, new Set());
    _listeners.get(type)!.add(cb);
    return () => _listeners.get(type)?.delete(cb);
  },
  emit(type: string, payload?: unknown): void {
    _listeners.get(type)?.forEach(cb => {
      try { cb({ type, payload }); } catch (e) { console.error('EventBus error:', e); }
    });
  },
  clearType(type: string): void {
    _listeners.delete(type);
  },
};

// --- TaskTracker: global task state (survives page navigation) ---

export interface TrackedTask {
  id: string;
  name: string;
  type: string;
  status: string;
  startedAt: number;
  result?: string;
  meta?: Record<string, any>;
}

const _activeTasks = new Map<string, TrackedTask>();

export const TaskTracker = {
  add(task: TrackedTask) {
    _activeTasks.set(task.id, task);
    EventBus.emit('tasktracker:change', { tasks: this.list() });
  },
  update(taskId: string, patch: Partial<TrackedTask>) {
    const t = _activeTasks.get(taskId);
    if (t) { Object.assign(t, patch); EventBus.emit('tasktracker:change', { tasks: this.list() }); }
  },
  remove(taskId: string) {
    _activeTasks.delete(taskId);
    EventBus.emit('tasktracker:change', { tasks: this.list() });
  },
  get(taskId: string) { return _activeTasks.get(taskId); },
  list() { return [..._activeTasks.values()]; },
  hasRunning(type?: string): boolean {
    for (const t of _activeTasks.values()) {
      if ((t.status === 'running' || t.status === 'queued') && (!type || t.type === type)) return true;
    }
    return false;
  },
  activeCount(type?: string): number {
    let n = 0;
    for (const t of _activeTasks.values()) {
      if ((t.status === 'running' || t.status === 'queued') && (!type || t.type === type)) n++;
    }
    return n;
  },
  isPanelGenerating(panelId: number, type?: string): boolean {
    for (const t of _activeTasks.values()) {
      if (t.meta?.panelId === panelId && (t.status === 'running' || t.status === 'queued') && (!type || t.type === type)) return true;
    }
    return false;
  },
  getGeneratingPanelIds(projectId: string, type: string): Set<number> {
    const ids = new Set<number>();
    for (const t of _activeTasks.values()) {
      if (t.meta?.projectId === projectId && t.type === type && (t.status === 'running' || t.status === 'queued') && t.meta?.panelId != null) ids.add(t.meta.panelId);
    }
    return ids;
  },
  findByMeta(key: string, value: any): TrackedTask[] {
    return [..._activeTasks.values()].filter(t => t.meta?.[key] === value && (t.status === 'running' || t.status === 'queued'));
  },
};

EventBus.on('task:update', (ev: any) => {
  const d = (ev as any).payload || ev;
  if (!d.task_id) return;
  const existing = TaskTracker.get(d.task_id);
  if (!existing) return;
  TaskTracker.update(d.task_id, { status: d.status });
  if (d.status === 'completed' || d.status === 'failed' || d.status === 'cancelled') {
    const label = d.status === 'completed' ? '\u5b8c\u6210' : d.status === 'cancelled' ? '\u5df2\u53d6\u6d88' : '\u5931\u8d25';
    showToast(existing.name + ' ' + label, d.status === 'completed' ? 'success' : d.status === 'cancelled' ? 'info' : 'error');
    setTimeout(() => TaskTracker.remove(d.task_id), 5000);
  }
});

export interface Account {
  id: string;
  name: string;
  avatar: string;
  is_default: boolean;
  username?: string;
  is_admin?: boolean;
}

let _accessToken: string = Storage.get<string>('accessToken', '');
let _refreshToken: string = Storage.get<string>('refreshToken', '');
let _currentAccountId: string = Storage.get<string>('accountId', '');
let _isAdmin: boolean = Storage.get<boolean>('isAdmin', false);
let _refreshing: Promise<boolean> | null = null;
let _ws: WebSocket | null = null;
let _wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _wsHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
let _wsReconnectAttempt = 0;
let _wsTaskRefreshPending = false;
const _WS_RECONNECT_BASE = 2000;
const _WS_RECONNECT_MAX = 30000;
const _WS_HEARTBEAT_INTERVAL = 30000;

export const AccountManager = {
  get currentId(): string { return _currentAccountId; },
  get isAdmin(): boolean { return _isAdmin; },
  get accessToken(): string { return _accessToken; },
  get isLoggedIn(): boolean { return !!_accessToken; },

  setAuth(data: { access_token: string; refresh_token: string; account_id: string; is_admin: boolean }) {
    _accessToken = data.access_token;
    _refreshToken = data.refresh_token;
    _currentAccountId = data.account_id;
    _isAdmin = data.is_admin;
    Storage.set('accessToken', _accessToken);
    Storage.set('refreshToken', _refreshToken);
    Storage.set('accountId', _currentAccountId);
    Storage.set('isAdmin', _isAdmin);
    EventBus.emit('account:change', { id: _currentAccountId });
    this.connectWs();
  },

  async login(username: string, password: string): Promise<void> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: '登录失败' }));
      throw new Error(err.detail || `登录失败 (${res.status})`);
    }
    const data = await res.json();
    this.setAuth(data);
  },

  async register(username: string, password: string, name: string, phone: string, sms_code: string): Promise<void> {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, name, phone, sms_code }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: '注册失败' }));
      throw new Error(err.detail || `注册失败 (${res.status})`);
    }
    const data = await res.json();
    this.setAuth(data);
  },

  logout() {
    _accessToken = '';
    _refreshToken = '';
    _currentAccountId = '';
    _isAdmin = false;
    Storage.remove('accessToken');
    Storage.remove('refreshToken');
    Storage.remove('accountId');
    Storage.remove('isAdmin');
    this.disconnectWs();
    EventBus.emit('account:logout', {});
    window.location.href = '/login';
  },

  async refreshTokens(): Promise<boolean> {
    if (!_refreshToken) return false;
    if (_refreshing) return _refreshing;
    _refreshing = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: _refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        this.setAuth(data);
        return true;
      } catch {
        return false;
      } finally {
        _refreshing = null;
      }
    })();
    return _refreshing;
  },

  async ensureAccount(): Promise<string> {
    if (!_accessToken) {
      throw new Error('NOT_LOGGED_IN');
    }
    const res = await api('/api/auth/me');
    if (res.ok) {
      const data = await res.json();
      _currentAccountId = data.id;
      _isAdmin = !!data.is_admin;
      Storage.set('accountId', _currentAccountId);
      Storage.set('isAdmin', _isAdmin);
      this.connectWs();
      return _currentAccountId;
    }
    throw new Error('NOT_LOGGED_IN');
  },

  connectWs() {
    if (!_accessToken) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/api/ws/${_accessToken}`;
    if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) {
      if (_ws.url === url) return;
      _ws.onclose = null;
      _ws.close();
      _ws = null;
    }
    try {
      _ws = new WebSocket(url);
      _ws.onopen = () => {
        const wasReconnect = _wsReconnectAttempt > 0;
        _wsReconnectAttempt = 0;
        if (wasReconnect) {
          EventBus.emit('ws:reconnected', {});
          setTimeout(() => {
            window.dispatchEvent(new Event('tasks:refresh'));
            window.dispatchEvent(new Event('billing:refresh'));
          }, 500);
        }
        if (_wsHeartbeatTimer) clearInterval(_wsHeartbeatTimer);
        _wsHeartbeatTimer = setInterval(() => {
          if (_ws?.readyState === WebSocket.OPEN) {
            _ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, _WS_HEARTBEAT_INTERVAL);
      };
      _ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'concurrency_update') {
            EventBus.emit('concurrency:change', {
              running: msg.running,
              max: msg.max_concurrency,
              available: msg.available,
            });
          } else if (msg.type === 'balance_update' || msg.type === 'billing:update') {
            window.dispatchEvent(new Event('billing:refresh'));
          } else if (msg.type === 'task_update' || msg.type === 'task:update') {
            EventBus.emit('task:update', msg);
            if (!_wsTaskRefreshPending) {
              _wsTaskRefreshPending = true;
              setTimeout(() => {
                _wsTaskRefreshPending = false;
                window.dispatchEvent(new Event('tasks:refresh'));
              }, 2000);
            }
          } else if (msg.type === 'reorder_update') {
            EventBus.emit('reorder:update', msg);
          } else if (msg.type === 'asr_update') {
            EventBus.emit('asr:update', msg);
          } else if (msg.type === 'video_done') {
            EventBus.emit('video_done', msg);
          } else if (msg.type === 'video_failed') {
            EventBus.emit('video_failed', msg);
          } else if (msg.type === 'project_update') {
            EventBus.emit('project:update', msg);
          } else if (msg.type === 'library_update') {
            EventBus.emit('library:update', msg);
          } else if (msg.type === 'llm_chunk') {
            EventBus.emit('llm:chunk', msg);
          } else if (msg.type === 'llm_done') {
            EventBus.emit('llm:done', msg);
          } else if (msg.type === 'task_progress') {
            EventBus.emit('task:progress', msg);
          } else if (msg.type === 'tts_line_done') {
            EventBus.emit('tts:line_done', msg);
          } else if (msg.type === 'batch_item_done') {
            EventBus.emit('batch:item_done', msg);
          } else if (msg.type === 'batch_finish') {
            EventBus.emit('batch:finish', msg);
          } else if (msg.type === 'image_done') {
            EventBus.emit('image:done', msg);
          } else if (msg.type === 'pong') {
            /* heartbeat response */
          }
        } catch {}
      };
      _ws.onclose = () => {
        _ws = null;
        if (_wsHeartbeatTimer) { clearInterval(_wsHeartbeatTimer); _wsHeartbeatTimer = null; }
        if (_accessToken) {
          const delay = Math.min(_WS_RECONNECT_BASE * Math.pow(2, _wsReconnectAttempt), _WS_RECONNECT_MAX);
          _wsReconnectAttempt++;
          _wsReconnectTimer = setTimeout(async () => {
            if (_refreshToken) {
              const ok = await this.refreshTokens();
              if (!ok) return;
            }
            this.connectWs();
          }, delay);
        }
      };
      _ws.onerror = () => { _ws?.close(); };
    } catch {}
  },

  disconnectWs() {
    if (_wsReconnectTimer) { clearTimeout(_wsReconnectTimer); _wsReconnectTimer = null; }
    if (_wsHeartbeatTimer) { clearInterval(_wsHeartbeatTimer); _wsHeartbeatTimer = null; }
    _wsReconnectAttempt = 0;
    if (_ws) { _ws.onclose = null; _ws.close(); _ws = null; }
  },
};

let _toastTimeout: ReturnType<typeof setTimeout> | null = null;

function _showToast(msg: string, type: 'error' | 'warn' | 'info' | 'success' = 'error') {
  let el = document.getElementById('_global-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = '_global-toast';
    el.style.cssText = `
      position: fixed; top: 24px; left: 50%; transform: translateX(-50%) translateY(-20px);
      z-index: 99999; padding: 12px 24px; border-radius: 10px; color: oklch(99% 0.004 285);
      font-size: 14px; font-weight: 500; display: flex; align-items: center; gap: 10px;
      max-width: min(90vw, 420px); box-sizing: border-box;
      box-shadow: 0 4px 12px oklch(15% 0.01 285 / 0.08); transition: all 250ms cubic-bezier(0.25, 1, 0.5, 1);
      opacity: 0; pointer-events: none;
    `;
    document.body.appendChild(el);
  }

  let icon = 'error';
  let bg = 'rgba(239, 68, 68, 0.9)';
  if (type === 'warn') { bg = 'rgba(245, 158, 11, 0.9)'; icon = 'warning'; }
  else if (type === 'info') { bg = 'rgba(59, 130, 246, 0.9)'; icon = 'info'; }
  else if (type === 'success') { bg = 'rgba(34, 197, 94, 0.9)'; icon = 'check_circle'; }

  el.style.background = bg;
  el.style.border = `1px solid ${bg.replace('0.9', '1')}`;

  el.innerHTML = `<span class="material-symbols-outlined" style="font-size: 20px;">${icon}</span> <span>${msg}</span>`;

  el.style.opacity = '1';
  el.style.transform = 'translateX(-50%) translateY(0)';

  if (_toastTimeout) clearTimeout(_toastTimeout);
  _toastTimeout = setTimeout(() => {
    el!.style.opacity = '0';
    el!.style.transform = 'translateX(-50%) translateY(-20px)';
  }, 4000);
}

export function showToast(msg: string, type: 'error' | 'warn' | 'info' | 'success' = 'info') {
  _showToast(msg, type);
}

export interface ApiOptions extends RequestInit {
  silent?: boolean;
}

export function api(path: string, opts: ApiOptions = {}): Promise<Response> {
  const _apiStart = performance.now();
  const headers = new Headers(opts.headers);
  if (!headers.has('Content-Type') && opts.body && !(opts.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (_accessToken) {
    headers.set('Authorization', `Bearer ${_accessToken}`);
  } else if (_currentAccountId) {
    headers.set('X-Account-Id', _currentAccountId);
  }
  const { silent, ...fetchOpts } = opts;
  return fetch(path, { ...fetchOpts, headers, cache: 'no-store' as RequestCache }).then(async res => {
    if (res.status === 401 && _refreshToken) {
      const ok = await AccountManager.refreshTokens();
      if (ok) {
        headers.set('Authorization', `Bearer ${_accessToken}`);
        return fetch(path, { ...fetchOpts, headers });
      }
      AccountManager.logout();
    }
    if (!res.ok && !silent) {
      const status = res.status;
      if (status === 429 || status === 402) {
        const body = await res.clone().json().catch(() => ({}));
        _showToast(body.detail || (status === 429 ? '并发已满，请等待任务完成后再试' : '余额不足，请充值后继续'), 'error');
      } else if (status === 403) {
        _showToast('没有权限执行此操作', 'error');
      } else if (status >= 500) {
        _showToast(`服务器错误 (${status})`, 'error');
      }
    }
    if (path !== '/api/logs') logApiCall(path, opts.method || 'GET', res.status, Math.round(performance.now() - _apiStart));
    return res;
  }).catch(err => {
    if (err.name !== 'AbortError' && !silent) {
      _showToast('网络连接失败，请检查网络', 'error');
    }
    throw err;
  });
}

export function createAbortController(): AbortController {
  return new AbortController();
}

export async function downloadBlob(apiPath: string, filename: string): Promise<void> {
  const res = await api(apiPath, { silent: true });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `下载失败 (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function debounce<T extends (...args: any[]) => void>(fn: T, delay = 300): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export function startPolling(fn: () => void, intervalMs: number): () => void {
  let timer: ReturnType<typeof setInterval> | null = null;
  const start = () => {
    if (!timer) { fn(); timer = setInterval(fn, intervalMs); }
  };
  const stop = () => {
    if (timer) { clearInterval(timer); timer = null; }
  };
  const onVisibility = () => { document.hidden ? stop() : start(); };
  document.addEventListener('visibilitychange', onVisibility);
  start();
  return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
}

export interface ImageGenOptions {
  prompt: string;
  model?: string;
  aspectRatio?: string;
  size?: string;
  n?: number;
  responseFormat?: 'url' | 'b64_json';
  subjectReference?: { type: string; imageFile: string }[];
}

export interface ImageGenResult {
  imageUrls: string[];
  imageBase64: string[];
  successCount: number;
  failedCount: number;
  traceId: string;
}

async function _callImageApi(opts: ImageGenOptions): Promise<ImageGenResult> {
  const res = await api('/api/image/generate', {
    method: 'POST',
    body: JSON.stringify({
      prompt: opts.prompt,
      model: opts.model || '',
      size: opts.size || '1024x1024',
      aspect_ratio: opts.aspectRatio || '',
      n: opts.n || 1,
      response_format: opts.responseFormat || 'url',
      subject_reference: opts.subjectReference?.map(ref => ({
        type: ref.type,
        image_file: ref.imageFile,
      })),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `图片生成失败 (${res.status})`);
  }
  const data = await res.json();
  return {
    imageUrls: data.image_urls || [],
    imageBase64: data.image_base64 || [],
    successCount: data.success_count || 0,
    failedCount: data.failed_count || 0,
    traceId: data.trace_id || '',
  };
}

export const ImageService = {
  textToImage(opts: ImageGenOptions): Promise<ImageGenResult> {
    return _callImageApi(opts);
  },

  imageToImage(opts: ImageGenOptions): Promise<ImageGenResult> {
    if (!opts.subjectReference?.length) {
      return Promise.reject(new Error('图生图需要至少一张参考图片'));
    }
    return _callImageApi(opts);
  },
};


export interface StreamCallbacks {
  onChunk?: (text: string) => void;
  onDone?: (data: { done: true; model?: string; usage?: any; full_content?: string }) => void;
  onError?: (msg: string) => void;
  onTaskCreated?: (taskId: string) => void;
}

export async function wsTask(
  path: string,
  init: RequestInit,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
  taskMeta?: Record<string, any>,
): Promise<void> {
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (_accessToken) {
    headers.set('Authorization', `Bearer ${_accessToken}`);
  } else if (_currentAccountId) {
    headers.set('X-Account-Id', _currentAccountId);
  }

  let res: Response;
  try {
    res = await fetch(path, { ...init, method: 'POST', headers, signal });
  } catch (err: any) {
    if (signal?.aborted) { callbacks.onError?.('aborted'); return; }
    callbacks.onError?.(err.message || '网络连接失败');
    return;
  }

  if (res.status === 401 && _refreshToken) {
    const ok = await AccountManager.refreshTokens();
    if (ok) {
      headers.set('Authorization', `Bearer ${_accessToken}`);
      try {
        res = await fetch(path, { ...init, method: 'POST', headers, signal });
      } catch (err: any) {
        if (signal?.aborted) { callbacks.onError?.('aborted'); return; }
        callbacks.onError?.(err.message || '网络连接失败');
        return;
      }
    } else {
      AccountManager.logout();
      return;
    }
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch {}
    callbacks.onError?.(detail);
    return;
  }

  const resData = await res.json();
  const taskId = resData.task_id;

  // Handle batch responses (task_ids array): listen for each sub-task via WebSocket
  if (!taskId && resData.task_ids && Array.isArray(resData.task_ids)) {
    const batchTaskIds = resData.task_ids as string[];
    const batchType = resData.type || taskMeta?.type || 'image';
    const batchName = resData.task_name || '\u6279\u91cf\u4efb\u52a1';
    const pending = new Set(batchTaskIds);
    let batchCompleted = 0, batchFailed = 0;

    for (const tid of batchTaskIds) {
      TaskTracker.add({
        id: tid, name: batchName, type: batchType, status: 'queued',
        startedAt: Date.now(),
        meta: { ...taskMeta, batch: true, batchId: resData.batch_id },
      });
    }

    try { callbacks.onTaskCreated?.(resData.batch_id || batchTaskIds[0]); } catch {}

    callbacks.onChunk?.(JSON.stringify({
      event: 'start', batch_id: resData.batch_id, total: batchTaskIds.length,
    }));

    return new Promise<void>((resolve) => {
      let settled = false;
      const unsubs: (() => void)[] = [];
      let _batchPollTimer: ReturnType<typeof setInterval> | null = null;
      const cleanup = () => { if (settled) return; settled = true; if (_batchPollTimer) clearInterval(_batchPollTimer); unsubs.forEach(u => u()); resolve(); };

      unsubs.push(EventBus.on('task:update', (ev: any) => {
        const d = (ev as any).payload || ev;
        if (!d.task_id || !pending.has(d.task_id)) return;
        if (d.status === 'completed') {
          batchCompleted++;
          pending.delete(d.task_id);
          callbacks.onChunk?.(JSON.stringify({
            event: 'item_done', item_id: d.item_id || d.task_id,
            image_url: d.image_url || d.response_content || d.video_url || '',
            index: batchTaskIds.indexOf(d.task_id),
          }));
        } else if (d.status === 'failed') {
          batchFailed++;
          pending.delete(d.task_id);
          callbacks.onChunk?.(JSON.stringify({
            event: 'item_fail', item_id: d.item_id || d.task_id,
            error: d.error || d.detail || '\u751f\u6210\u5931\u8d25',
            index: batchTaskIds.indexOf(d.task_id),
          }));
        } else {
          return;
        }
        callbacks.onChunk?.(JSON.stringify({
          event: 'progress', completed: batchCompleted, failed: batchFailed, total: batchTaskIds.length,
        }));
        if (pending.size === 0) {
          callbacks.onChunk?.(JSON.stringify({
            event: 'finish', done: batchCompleted, failed: batchFailed, total: batchTaskIds.length,
          }));
          callbacks.onDone?.({ done: true, ...resData });
          cleanup();
        }
      }));

      unsubs.push(EventBus.on('ws:reconnected', () => {
        if (settled) return;
        setTimeout(async () => {
          if (settled) return;
          for (const tid of [...pending]) {
            try {
              const r = await api('/api/tasks/' + tid, { silent: true });
              if (!r.ok) continue;
              const data = await r.json();
              if (data.status === 'completed' || data.status === 'failed') {
                EventBus.emit('task:update', { task_id: tid, status: data.status, image_url: data.response_content, item_id: data.item_id || '', detail: data.detail });
              }
            } catch {}
          }
        }, 1000);
      }));

      _batchPollTimer = setInterval(async () => {
        if (settled || pending.size === 0) { if (_batchPollTimer) { clearInterval(_batchPollTimer); _batchPollTimer = null; } return; }
        for (const tid of [...pending]) {
          try {
            const r = await api('/api/tasks/' + tid, { silent: true });
            if (!r.ok) continue;
            const data = await r.json();
            if (data.status === 'completed' || data.status === 'failed') {
              EventBus.emit('task:update', { task_id: tid, status: data.status, image_url: data.response_content, item_id: data.item_id || '', detail: data.detail });
            }
          } catch {}
        }
      }, 5000);

      signal?.addEventListener('abort', () => { callbacks.onError?.('aborted'); cleanup(); });
      setTimeout(() => { if (!settled) { callbacks.onError?.('\u6279\u91cf\u4efb\u52a1\u8d85\u65f6'); cleanup(); } }, 1800000);
    });
  }

  if (!taskId) {
    callbacks.onDone?.({ done: true, ...resData });
    return;
  }

  TaskTracker.add({
    id: taskId,
    name: resData.task_name || resData.name || '\u4efb\u52a1',
    type: resData.type || 'unknown',
    status: 'queued',
    startedAt: Date.now(),
    meta: taskMeta,
  });

  try { callbacks.onTaskCreated?.(taskId); } catch {}

  return new Promise<void>((resolve) => {
    let settled = false;
    const unsubs: (() => void)[] = [];
    let _taskPollTimer: ReturnType<typeof setInterval> | null = null;

    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (_taskPollTimer) clearInterval(_taskPollTimer);
      unsubs.forEach(u => u());
      resolve();
    };

    unsubs.push(EventBus.on('llm:chunk', (ev: any) => {
      const d = (ev as any).payload || ev;
      if (d.task_id === taskId) {
        callbacks.onChunk?.(d.content, d.is_reasoning ? { is_reasoning: true } : undefined);
      }
    }));

    unsubs.push(EventBus.on('llm:done', (ev: any) => {
      const d = (ev as any).payload || ev;
      if (d.task_id === taskId) {
        const finalStatus = d.error ? 'failed' : 'completed';
        TaskTracker.update(taskId, { status: finalStatus });
        setTimeout(() => TaskTracker.remove(taskId), 5000);
        callbacks.onDone?.({
          done: true,
          ...d,
          usage: { total_tokens: (d.input_tokens || 0) + (d.output_tokens || 0) },
        });
        cleanup();
      }
    }));

    unsubs.push(EventBus.on('task:update', (ev: any) => {
      const d = (ev as any).payload || ev;
      if (d.task_id === taskId) {
        if (d.status === 'completed') {
          const doneData: any = { done: true, ...resData, ...d };
          if (d.image_url && !d.image_urls) {
            doneData.image_urls = [d.image_url];
          }
          callbacks.onDone?.(doneData);
          cleanup();
        } else if (d.status === 'failed') {
          callbacks.onError?.(d.error || d.detail || '任务失败');
          cleanup();
        }
      }
    }));

    unsubs.push(EventBus.on('task:progress', (ev: any) => {
      const d = (ev as any).payload || ev;
      if (d.task_id === taskId) {
        callbacks.onChunk?.(JSON.stringify(d));
      }
    }));

    unsubs.push(EventBus.on('tts:line_done', (ev: any) => {
      const d = (ev as any).payload || ev;
      if (d.task_id === taskId) {
        callbacks.onChunk?.(JSON.stringify(d));
      }
    }));

    unsubs.push(EventBus.on('ws:reconnected', () => {
      if (settled) return;
      setTimeout(async () => {
        if (settled) return;
        try {
          const r = await api(`/api/tasks/${taskId}`, { silent: true });
          if (r.ok) {
            const data = await r.json();
            if (data.status === 'completed') {
              const doneData: any = { done: true, ...data };
              if (data.response_content && !data.image_urls) {
                doneData.image_urls = [data.response_content];
              }
              callbacks.onDone?.(doneData);
              cleanup();
            } else if (data.status === 'failed') {
              callbacks.onError?.(data.detail || '任务失败');
              cleanup();
            }
          }
        } catch {}
      }, 1000);
    }));

    _taskPollTimer = setInterval(async () => {
      if (settled) { if (_taskPollTimer) { clearInterval(_taskPollTimer); _taskPollTimer = null; } return; }
      try {
        const r = await api(`/api/tasks/${taskId}`, { silent: true });
        if (!r.ok) return;
        const data = await r.json();
        if (data.status === 'completed' || data.status === 'failed') {
          EventBus.emit('task:update', { task_id: taskId, status: data.status, image_url: data.response_content, item_id: data.item_id || '', detail: data.detail });
        }
      } catch {}
    }, 5000);

    signal?.addEventListener('abort', () => {
      callbacks.onError?.('aborted');
      cleanup();
    });

    setTimeout(() => {
      if (!settled) {
        callbacks.onError?.('任务超时');
        cleanup();
      }
    }, 1830000);
  });
}

export function extractDetail(detail: unknown, fallback: string = 'Unknown error'): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || String(d)).join('; ');
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return fallback;
}

// ---------------------------------------------------------------------------
//  mutate() — unified mutation wrapper with de-dup, optimistic rollback, toast
// ---------------------------------------------------------------------------

const _inflightMutations = new Set<string>();

export interface MutateOptions<T> {
  key: string;
  fn: () => Promise<Response>;
  onSuccess?: (data: T) => void;
  onError?: (err: string) => void;
  optimistic?: {
    apply: () => void;
    rollback: () => void;
  };
  toast?: { success?: string; error?: string };
  silent?: boolean;
}

export async function mutate<T = any>(opts: MutateOptions<T>): Promise<T | null> {
  if (_inflightMutations.has(opts.key)) {
    if (!opts.silent) _showToast('操作进行中，请稍候', 'warn');
    return null;
  }
  _inflightMutations.add(opts.key);
  opts.optimistic?.apply();
  try {
    const res = await opts.fn();
    if (!res.ok) {
      const body = await res.clone().json().catch(() => ({}));
      const msg = extractDetail(body.detail, opts.toast?.error || '操作失败');
      opts.optimistic?.rollback();
      if (!opts.silent) _showToast(msg, 'error');
      opts.onError?.(msg);
      return null;
    }
    const data: T | null = res.status !== 204
      ? await res.json().catch(() => null)
      : null;
    opts.onSuccess?.(data as T);
    if (opts.toast?.success) _showToast(opts.toast.success, 'success');
    return data;
  } catch (err: any) {
    opts.optimistic?.rollback();
    const msg = err?.message || '网络连接失败';
    if (!opts.silent) _showToast(msg, 'error');
    opts.onError?.(msg);
    return null;
  } finally {
    _inflightMutations.delete(opts.key);
  }
}

// ---------------------------------------------------------------------------
//  useQuery() — query manager with dedup, stale-on-visibility, event refresh
// ---------------------------------------------------------------------------

const _inflightQueries = new Map<string, Promise<any>>();

export interface QueryOptions<T> {
  key: string;
  fn: () => Promise<T>;
  staleTime?: number;
  pollInterval?: number;
  refreshOn?: string[];
  onData?: (data: T) => void;
  onError?: (err: string) => void;
}

export interface QueryHandle<T> {
  refresh: () => Promise<void>;
  destroy: () => void;
}

export function useQuery<T>(opts: QueryOptions<T>): QueryHandle<T> {
  const staleTime = opts.staleTime ?? 30_000;
  let lastFetch = 0;
  let destroyed = false;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const unsubs: (() => void)[] = [];

  const doFetch = async () => {
    if (destroyed) return;
    const existing = _inflightQueries.get(opts.key);
    if (existing) { await existing; return; }
    const promise = (async () => {
      try {
        const data = await opts.fn();
        lastFetch = Date.now();
        if (!destroyed) opts.onData?.(data);
      } catch (err: any) {
        if (!destroyed) opts.onError?.(err?.message || '加载失败');
      } finally {
        _inflightQueries.delete(opts.key);
      }
    })();
    _inflightQueries.set(opts.key, promise);
    await promise;
  };

  const startPoll = () => {
    if (opts.pollInterval && !pollTimer) {
      pollTimer = setInterval(doFetch, opts.pollInterval);
    }
  };
  const stopPoll = () => {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  };

  const onVisibility = () => {
    if (document.hidden) {
      stopPoll();
    } else {
      if (Date.now() - lastFetch > staleTime) doFetch();
      startPoll();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  unsubs.push(() => document.removeEventListener('visibilitychange', onVisibility));

  if (opts.refreshOn) {
    for (const evt of opts.refreshOn) {
      unsubs.push(EventBus.on(evt, () => doFetch()));
    }
  }

  doFetch();
  startPoll();

  return {
    refresh: doFetch,
    destroy: () => {
      destroyed = true;
      stopPoll();
      unsubs.forEach(u => u());
    },
  };
}

// ---------------------------------------------------------------------------
//  guardClick() — prevent duplicate submissions on button clicks
// ---------------------------------------------------------------------------

export function guardClick(
  host: any,
  flagProp: string,
  fn: () => Promise<void>,
): (e: Event) => void {
  return async () => {
    if (host[flagProp]) return;
    host[flagProp] = true;
    if (host.requestUpdate) host.requestUpdate();
    try {
      await fn();
    } finally {
      host[flagProp] = false;
      if (host.requestUpdate) host.requestUpdate();
    }
  };
}
